import React, { ChangeEvent, useContext, useState, useEffect } from 'react';
import { useApolloClient, useQuery } from '@apollo/react-hooks';
import { useConfigSelectors, useFilterDataSelectors, useLanguageSelectors } from 'src/app/reducers';
import { useFilterSelectors } from 'src/@complaints/reducers';
import { ComplaintsDispatchContext } from 'src/@complaints/providers/ComplaintsStateProvider';
import { Filter, IDFilter } from 'src/componentsMigrated';
import {
  FilterRow,
  RangeSelector,
  validateTimeFormat,
  isValidTime,
  isCompletedTimeFormat,
  onCloseTimeDialogValidation,
  PersonFilter,
} from '@ems/client-design-system';
import {
  updateSelectedFilterItems,
  clearSelectedFilterItems,
  updateRangeFilter,
  updateComplainerFilter,
  updateComplaintIDFilter,
  getComplainersByPartialFullName,
} from 'src/@complaints/actions';
import { IFilterItem, IComplainer, IComplainerFilterItem } from 'src/@complaints/interfaces';
import { getIfFiltersEmpty, formatFilterItems, translateLabels } from 'src/@complaints/functions';
import { capitalizeObjectKeys } from 'src/utils/objectModifiers';
import { HAS_OPERATION } from 'src/constants';
import { GET_COMPLAINER_BY_ID } from 'src/app/gql/queries/complainer/getComplainer';
import { GetComplainerQuery, GetComplainerQueryVariables } from 'src/app/gql/__generated__/hooks';

export const FilterContainer = () => {
  const client = useApolloClient();
  const dispatcher = useContext(ComplaintsDispatchContext);
  const configSelectors = useConfigSelectors();
  const {
    complaints: {
      filter: { availableCategoryList },
    },
  } = configSelectors.getConfig();
  const filterSelectors = useFilterSelectors();
  const filteredItems = filterSelectors.getRegularFilters();
  const complainerItem = filterSelectors.getComplainerFilter();
  const selectedComplaintID = filterSelectors.getComplaintID();

  const languageSelectors = useLanguageSelectors();
  const {
    components: {
      labels: {
        complainer,
        inquiryID,
        filters: { clear: clearValue, filter: filterValue, clearFilters },
      },
      hints: { noMatchesFound, loading },
      filters: filtersTranslation,
      lists: { complaintsFilterCategories, reason, status, correlated },
    },
  } = languageSelectors.getLanguage();

  const convertedDataList = capitalizeObjectKeys({
    ...reason,
    ...status,
    ...correlated,
  });

  const filtersDataSelectors = useFilterDataSelectors();
  const complaintsFilterData = filtersDataSelectors.getComplaintsFilterData();

  const convertedFilterData = {};
  Object.entries(complaintsFilterData).map(([key, value]) => {
    convertedFilterData[key] = formatFilterItems(value);
  });

  const { value: timeFilters, label: timeLabel } = filterSelectors.getTimeFilter();

  const filterComponents: JSX.Element[] = [];

  // Complainer Filter
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchData, setSearchData] = useState<IComplainer[]>([]);
  const [idSearchValue, setIDSearchValue] = useState<string>('');
  const [isLoadingComplainers, setIsLoadingComplainers] = useState<boolean>(false);

  const [complainerItemState, setComplainerItemState] = useState<IComplainerFilterItem | null>(
    null
  );

  useEffect(() => {
    if (complainerItem?.id) {
      setComplainerItemState(complainerItem);
      console.log('complainerItem useEffect', complainerItem);
    }
  }, [complainerItem]);

  useQuery<GetComplainerQuery, GetComplainerQueryVariables>(GET_COMPLAINER_BY_ID, {
    variables: { id: Number(complainerItem?.id) || 0 },
    skip: !complainerItem?.id || complainerItem.name !== null,
    onCompleted: data => {
      const complainer = data?.complainer;
      if (complainer && complainer.id && typeof complainer.id === 'number') {
        setComplainerItemState({
          id: complainer?.id,
          name: complainer
            ? [complainer.firstName, complainer.lastName].filter(Boolean).join(' ')
            : '',
        });
      }
    },
  });

  const onComplainerClearSelect = () => {
    updateComplainerFilter(null, dispatcher);
    setSearchData([]);
    setComplainerItemState(null);
  };
  const onIDClearSelect = () => {
    setIDSearchValue('');
    updateComplaintIDFilter('', dispatcher);
  };

  const onComplainerItemSelect = (item: IComplainer) => {
    const nameArray: string[] = [];
    [item.firstName, item.lastName].forEach((field: string | null) => {
      if (field) {
        nameArray.push(field.trimRight());
      }
    });
    const name = nameArray.join(' ');

    const complainer: IComplainerFilterItem = {
      id: item.id,
      name,
    };
    updateComplainerFilter(complainer, dispatcher);
    setSearchValue('');
    setSearchData([]);
  };

  const onResultsReturned = (res: IComplainer[]) => {
    setSearchData(res);
    setIsLoadingComplainers(false);
  };

  const onSearchUpdate = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setSearchData([]);

    if (value !== '') {
      setIsLoadingComplainers(true);
      getComplainersByPartialFullName(client, value, onResultsReturned);
    }
  };

  useEffect(() => {
    // This makes sure that the value in the ID input is consistent with the selected ID
    // when the selected ID is saved in local storage.
    setIDSearchValue(selectedComplaintID);
  }, [selectedComplaintID]);

  const onIDChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    const re = /^[0-9]{1,18}$/;

    // Input must be positive number and has no more than 18 digits.
    if (value === '' || re.test(value)) {
      setIDSearchValue(value);
    }
  };

  const onIDSubmit = () => {
    if (idSearchValue !== '') {
      updateComplaintIDFilter(idSearchValue, dispatcher);
    }
  };

  const clearAllFilters = () => {
    setIDSearchValue('');
    clearSelectedFilterItems(dispatcher);
    setComplainerItemState(null);
  };

  availableCategoryList.map((category: string) => {
    if (category === 'time') {
      filterComponents.push(
        <RangeSelector
          key="filter-time-selector"
          type="time"
          min={null}
          max={null}
          fromInputValue={timeFilters.from}
          toInputValue={timeFilters.to}
          setInputValue={(value: string, field: 'from' | 'to') => {
            updateRangeFilter('time', value, field, dispatcher);
          }}
          label={timeLabel}
          validateInputFormat={validateTimeFormat}
          isValidValue={isValidTime}
          onCloseValidation={onCloseTimeDialogValidation}
          isCompletedValueFormat={isCompletedTimeFormat}
          languageData={filtersTranslation.time}
        />
      );
    } else if (category === 'complainer') {
      filterComponents.push(
        <PersonFilter
          key="filter-complainer-selector"
          searchData={searchData}
          searchValue={searchValue}
          onSearchUpdate={onSearchUpdate}
          onItemSelect={onComplainerItemSelect}
          //selectedData={complainerItem}
          selectedData={complainerItemState}
          onClearSelect={onComplainerClearSelect}
          isLoading={isLoadingComplainers}
          languageData={{
            title: complainer,
            noMatchesFound,
            loading,
          }}
        />
      );
    } else if (category === 'id') {
      filterComponents.push(
        <IDFilter
          categoryName={category}
          key="filter-id-selector"
          value={idSearchValue}
          onChange={onIDChange}
          onSubmit={onIDSubmit}
          selectedID={selectedComplaintID}
          onClearSelect={onIDClearSelect}
          languageData={{
            title: inquiryID,
            noMatchesFound,
            loading,
          }}
        />
      );
    } else {
      let originalItems = convertedFilterData[category];
      if (originalItems !== undefined) {
        originalItems = translateLabels(originalItems, convertedDataList);
        filterComponents.push(
          <Filter
            key={category}
            categoryName={complaintsFilterCategories[category]}
            filterItems={originalItems}
            selectedItems={filteredItems[category]}
            updateItems={(items: IFilterItem[]) =>
              updateSelectedFilterItems(category, items, dispatcher)
            }
            languageData={{ clearValue, filterValue, noMatchesFound }}
            iconCategories={[]}
            type={category === HAS_OPERATION ? 'single' : 'multiple'}
          />
        );
      }
    }
  });

  return (
    <div>
      <FilterRow
        filters={filterComponents}
        clearFilters={clearAllFilters}
        clearDisabled={getIfFiltersEmpty(
          filteredItems,
          timeFilters,
          complainerItem,
          selectedComplaintID
        )}
        languageData={{ clearFilters }}
      />
    </div>
  );
};
